/**
 * /campaigns — les campagnes de l'organisation, et leur ouverture.
 *
 * Une campagne = un audit consolidé : UN code pour N machines, un seul dossier,
 * une seule facture. Le code rendu par `createCampaign` est affiché ici une
 * seule fois ; il est ensuite transmis à la page de détail par l'état de
 * navigation, jamais relu depuis Firestore (où seule son empreinte existe).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Layers, Loader2, Plus } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useT } from '@/lib/i18n';
import { listCampaigns } from '@/lib/firestore';
import { callCreateCampaign } from '@/lib/functions';
import { campaignStatusKey, campaignStatusVariant, parseHostsInput } from '@/lib/campaigns';
import { fmtDate } from '@/lib/utils';
import type { CampaignDoc } from '@/types/models';
import { AlertBanner } from '@/components/AlertBanner';
import { ConsentCheckbox } from '@/components/ConsentCheckbox';
import { CampaignCodeCard, CampaignCommand } from '@/components/campaign/CampaignCode';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader } from '@/components/ui/loader';
import { Table, TableBody, TableCell, TableHead, TableHeadCell, TableRow } from '@/components/ui/table';

interface CreatedCampaign {
  campaignId: string;
  campaignCode: string;
  expiresAt: string;
}

/** Machines prêtes / annoncées d'une campagne, pour la colonne « Machines ». */
function machineCounts(campaign: CampaignDoc): { ready: number; total: number } {
  const coverage = campaign.consolidated?.coverage;
  const ready = coverage?.hostsAudited?.length ?? 0;
  return { ready, total: Math.max(campaign.hostsExpected?.length ?? 0, campaign.auditIds?.length ?? 0, ready) };
}

export default function Campaigns() {
  const { effectiveOrgId } = useAuth();
  const { t, locale } = useT();
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState<CampaignDoc[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [name, setName] = useState('');
  const [hostsRaw, setHostsRaw] = useState('');
  const [consent, setConsent] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedCampaign | null>(null);

  const reload = useCallback(() => {
    if (!effectiveOrgId) { setCampaigns([]); return; }
    setLoadError(null);
    listCampaigns(effectiveOrgId)
      .then(setCampaigns)
      .catch((e: Error) => { setCampaigns([]); setLoadError(e.message); });
  }, [effectiveOrgId]);

  useEffect(reload, [reload]);

  const hosts = useMemo(() => parseHostsInput(hostsRaw), [hostsRaw]);

  const openForm = () => {
    setName(t('campaign.form.name.default', { date: new Date().toLocaleDateString(locale === 'en' ? 'en-CA' : 'fr-CA') }));
    setHostsRaw('');
    setConsent(false);
    setCreated(null);
    setFormOpen(true);
  };

  const closeDialog = () => {
    setFormOpen(false);
    setCreated(null);
  };

  const submit = async () => {
    if (!effectiveOrgId) return toast.error(t('campaign.err.no_org'));
    if (!consent) return toast.error(t('upload.consent.required'));
    setCreating(true);
    try {
      const { data } = await callCreateCampaign({
        orgId: effectiveOrgId,
        displayName: name.trim(),
        // Les machines annoncées sont FACULTATIVES : on n'envoie la clé que
        // si l'utilisateur en a saisi, sinon le serveur reçoit un tableau vide
        // qui ne veut rien dire de plus qu'une absence.
        ...(hosts.length > 0 ? { hostsExpected: hosts } : {}),
        consent: true,
      });
      setCreated({ campaignId: data.campaignId, campaignCode: data.campaignCode, expiresAt: data.expiresAt });
      reload();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setCreating(false);
    }
  };

  /** Ouvre le détail en lui passant le code : il n'existe nulle part ailleurs. */
  const openCreated = () => {
    if (!created) return;
    const { campaignId, campaignCode, expiresAt } = created;
    closeDialog();
    navigate(`/campaigns/${campaignId}`, { state: { code: campaignCode, expiresAt } });
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl text-ink-900 dark:text-white">{t('campaign.title')}</h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-500">{t('campaign.subtitle')}</p>
        </div>
        <Button onClick={openForm}>
          <Plus className="h-4 w-4" /> {t('campaign.new')}
        </Button>
      </header>

      {loadError && <AlertBanner variant="danger">{loadError}</AlertBanner>}

      {campaigns === null ? (
        <Loader label={t('common.loading')} />
      ) : campaigns.length === 0 ? (
        <EmptyState
          icon={<Layers className="h-5 w-5" />}
          title={t('campaign.empty.title')}
          description={t('campaign.empty.desc')}
          action={<Button onClick={openForm}><Plus className="h-4 w-4" /> {t('campaign.new')}</Button>}
        />
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeadCell>{t('campaign.col.name')}</TableHeadCell>
              <TableHeadCell>{t('common.status')}</TableHeadCell>
              <TableHeadCell>{t('campaign.col.machines')}</TableHeadCell>
              <TableHeadCell>{t('common.created')}</TableHeadCell>
              <TableHeadCell>{t('campaign.col.expires')}</TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.map(campaign => {
              const counts = machineCounts(campaign);
              return (
                <TableRow
                  key={campaign.id}
                  onClick={() => navigate(`/campaigns/${campaign.id}`)}
                  className="cursor-pointer hover:bg-slate-50 dark:hover:bg-ink-700/40"
                >
                  <TableCell className="font-medium text-ink-900 dark:text-white">{campaign.displayName}</TableCell>
                  <TableCell>
                    <Badge variant={campaignStatusVariant(campaign.status)}>{t(campaignStatusKey(campaign.status))}</Badge>
                  </TableCell>
                  <TableCell className="text-ink-600 dark:text-ink-300">
                    {counts.total === 0
                      ? t('campaign.machines.none')
                      : t('campaign.machines.count', { ready: counts.ready, total: counts.total })}
                  </TableCell>
                  <TableCell className="text-ink-500">{fmtDate(campaign.createdAt, locale)}</TableCell>
                  <TableCell className="text-ink-500">{fmtDate(campaign.expiresAt, locale)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Dialog
        open={formOpen}
        onClose={closeDialog}
        title={created ? t('campaign.code.title') : t('campaign.form.title')}
        description={created ? undefined : t('campaign.form.desc')}
        footer={created ? (
          <>
            <Button variant="secondary" onClick={closeDialog}>{t('common.close')}</Button>
            <Button onClick={openCreated}>{t('campaign.open')}</Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={closeDialog} disabled={creating}>{t('common.cancel')}</Button>
            <Button onClick={submit} disabled={creating || !consent || !name.trim()}>
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {t('campaign.form.submit')}
            </Button>
          </>
        )}
      >
        {created ? (
          <div className="space-y-4">
            <CampaignCodeCard code={created.campaignCode} expiresAt={created.expiresAt} />
            <CampaignCommand code={created.campaignCode} />
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label htmlFor="campaign-name">{t('campaign.form.name')}</Label>
              <Input
                id="campaign-name"
                className="mt-1"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={t('campaign.form.name.ph')}
                disabled={creating}
              />
            </div>
            <div>
              <Label htmlFor="campaign-hosts">{t('campaign.form.hosts')}</Label>
              <textarea
                id="campaign-hosts"
                rows={3}
                className="input mt-1 resize-none"
                value={hostsRaw}
                onChange={e => setHostsRaw(e.target.value)}
                placeholder={t('campaign.form.hosts.ph')}
                disabled={creating}
              />
              <p className="mt-1 text-xs text-ink-500">{t('campaign.form.hosts.help')}</p>
              {hosts.length > 0 && (
                <p className="mt-1 text-xs text-brand-700 dark:text-brand-300">
                  {t('campaign.form.hosts.count', { n: hosts.length, list: hosts.join(', ') })}
                </p>
              )}
            </div>
            <ConsentCheckbox checked={consent} onChange={setConsent} disabled={creating} />
          </div>
        )}
      </Dialog>
    </div>
  );
}
